import { LIQUIDITY_STATE_LAYOUT_V4, MAINNET_PROGRAM_ID, SPL_ACCOUNT_LAYOUT, TokenAccount, WSOL } from '@raydium-io/raydium-sdk';
import {
    TOKEN_PROGRAM_ID,
    createBurnInstruction,
    createCloseAccountInstruction,
    createTransferInstruction,
    ASSOCIATED_TOKEN_PROGRAM_ID,
    Account,
    getAccount,
    TokenAccountNotFoundError,
    TokenInvalidAccountOwnerError,
    createAssociatedTokenAccountInstruction,
    TokenInvalidMintError,
    TokenInvalidOwnerError,
    getMinimumBalanceForRentExemptMint,
    MINT_SIZE,
    createInitializeMint2Instruction,
    createMintToInstruction,
    AuthorityType,
    createSetAuthorityInstruction,
    createFreezeAccountInstruction,
    createThawAccountInstruction,
    getAssociatedTokenAddressSync,
    unpackMint,
    Mint,
    getMultipleAccounts,
    createSyncNativeInstruction,
    NATIVE_MINT,
    createAssociatedTokenAccountIdempotentInstruction,
} from '@solana/spl-token';
import {
    AccountInfo,
    Commitment,
    ComputeBudgetProgram,
    ConfirmOptions,
    Connection,
    Keypair,
    LAMPORTS_PER_SOL,
    ParsedAccountData,
    PublicKey,
    RpcResponseAndContext,
    SignatureResult,
    Signer,
    SystemProgram,
    Transaction,
    TransactionInstruction,
    TransactionMessage,
    TransactionSignature,
    VersionedTransaction,
    sendAndConfirmTransaction,
} from '@solana/web3.js';
import { CreateMetadataAccountV3InstructionArgs, PROGRAM_ID, createCreateMetadataAccountV3Instruction } from '@metaplex-foundation/mpl-token-metadata';
import { utils } from '@project-serum/anchor';
import { useJito } from './useJito';
import bs58 from 'bs58';
import { removeKeypairDuplicates } from '../utils/remove-keypair-duplicates';
import { Metaplex } from '@metaplex-foundation/js';
import { USDC, USDT } from '../utils/constants';
import { burnerPriorityFee } from '../../ConfigFees';

export const useSplToken = () => {
    const { sendBundle, getTipInstructions } = useJito();

    async function getAllTokenAccountsByMint(connection: Connection, mint: string) {
        const response = await connection.getProgramAccounts(TOKEN_PROGRAM_ID, {
            filters: [
                {
                    dataSize: 165,
                },
                {
                    memcmp: {
                        offset: 0,
                        bytes: mint,
                    },
                },
            ],
            commitment: 'processed',
        });
        const allTokenAccounts = response.map((account) => account.pubkey);

        return allTokenAccounts;
    }

    async function getWalletTokenBalance(connection: Connection, wallet: PublicKey, mint: PublicKey) {
        console.log('wallet', wallet.toBase58(), 'mint', mint.toBase58());
        const tokenAccount = await getWalletTokenAccountByMint(connection, wallet, mint);
        console.log('tokenAccount', tokenAccount[0]);
        const account = await getAccount(connection, tokenAccount[0].pubkey, 'processed');
        return account.amount;
    }

    async function getWalletsTokenStringBalances(connection: Connection, wallets: PublicKey[], mint: PublicKey): Promise<string[]> {
        const walletTokenAccounts = await Promise.all(
            wallets.map(async (wallet) => {
                const tokenAccount = getAssociatedTokenAddressSync(mint, wallet);
                return tokenAccount;
            }),
        );

        const existingAccounts = walletTokenAccounts;
        const balances = await getTokenAccountsBalances(
            connection,
            existingAccounts.map((account) => account),
        );

        return balances;
    }

    async function getTokenDecimals(connection: Connection, publicKey: PublicKey) {
        const token = await connection.getParsedAccountInfo(publicKey, { commitment: 'processed' });
        const tokenDecimals = (token.value!.data as ParsedAccountData).parsed.info.decimals;
        return tokenDecimals as number;
    }

    async function getWalletTokenAccounts(connection: Connection, wallet: PublicKey): Promise<TokenAccount[]> {
        const walletTokenAccount = await connection.getTokenAccountsByOwner(
            wallet,
            {
                programId: TOKEN_PROGRAM_ID,
            },
            'processed',
        );
        return walletTokenAccount.value.map((i) => ({
            pubkey: i.pubkey,
            programId: i.account.owner,
            accountInfo: SPL_ACCOUNT_LAYOUT.decode(i.account.data),
        }));
    }

    async function getWalletTokenAccountByMint(connection: Connection, wallet: PublicKey, mint: PublicKey): Promise<TokenAccount[]> {
        const walletTokenAccount = await connection.getTokenAccountsByOwner(
            wallet,
            {
                mint,
                programId: TOKEN_PROGRAM_ID,
            },
            'processed',
        );
        return walletTokenAccount.value.map((i) => ({
            pubkey: i.pubkey,
            programId: i.account.owner,
            accountInfo: SPL_ACCOUNT_LAYOUT.decode(i.account.data),
        }));
    }

    async function fetchMarketAccountsByMints(
        connection: Connection,
        base: PublicKey,
        quote: PublicKey,
        commitment: 'processed' | 'confirmed' | 'singleGossip' | 'single' | 'recent' | 'max' = 'processed',
    ) {
        try {
            const accounts = await connection.getProgramAccounts(MAINNET_PROGRAM_ID.AmmV4, {
                commitment,
                filters: [
                    { dataSize: LIQUIDITY_STATE_LAYOUT_V4.span },
                    {
                        memcmp: {
                            offset: LIQUIDITY_STATE_LAYOUT_V4.offsetOf('baseMint'),
                            bytes: base.toBase58(),
                        },
                    },
                    {
                        memcmp: {
                            offset: LIQUIDITY_STATE_LAYOUT_V4.offsetOf('quoteMint'),
                            bytes: quote.toBase58(),
                        },
                    },
                ],
            });
            return accounts.map(({ pubkey, account }) => ({
                id: pubkey.toString(),
                ...LIQUIDITY_STATE_LAYOUT_V4.decode(account.data),
            }));
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    async function freezeLpOwnersWithWhitelist(
        connection: Connection,
        whitelist: string[], //wallets
        freezer: Keypair,
        token: string,
        freezerJitoTip: number,
        logMessage: (message: string, type?: 'success' | 'error') => void,
    ) {
        if (token === '') {
            logMessage('Token not deployed!');
            return;
        }
        logMessage('Freezing all token accounts with whitelist...');
        logMessage(`Whitelist size: ${whitelist.length} accounts...`);

        const freezedAccounts: string[] = [];

        async function getTokenAccountFromLpAccount(lpToken: PublicKey) {
            const account = await getAccount(connection, lpToken, 'processed');

            const tokenAccount = getAssociatedTokenAddressSync(new PublicKey(token), account.owner, true);
            return { account: tokenAccount, owner: account.owner };
        }

        let lpMint = '';

        while (true) {
            if (!lpMint) {
                const pool = (await fetchMarketAccountsByMints(connection, new PublicKey(token), new PublicKey(WSOL.mint)))[0];
                if (pool) {
                    logMessage(`LP found: ${pool.id}`);
                    lpMint = pool.lpMint.toBase58();
                }
            }
            let accountsFromLp: { account: PublicKey; owner: PublicKey }[] = [];
            if (lpMint) {
                const lpAccountsPromise = getAllTokenAccountsByMint(connection, lpMint);
                const lpAccounts = (await lpAccountsPromise).map((account) => account);
                const lpOwnersAccounts = await Promise.all(lpAccounts.map((lpAccount) => getTokenAccountFromLpAccount(lpAccount)));
                accountsFromLp.push(...lpOwnersAccounts);
                console.log(
                    'Accounts of LP owners',
                    accountsFromLp.map((account) => account.account.toBase58()),
                );
                accountsFromLp = accountsFromLp.filter(
                    (account) => !whitelist.includes(account.account.toBase58()) && !freezedAccounts.includes(account.account.toBase58()),
                );
                if (accountsFromLp.length) {
                    logMessage(`New LP holders accounts: ${accountsFromLp.length}`);
                }
            }
            if (accountsFromLp.length > 0) {
                if (accountsFromLp.length > 0) {
                    logMessage(`${accountsFromLp.length} new LP holders account(s) found.`);
                }
                await openAndFreezeTokenAccount(connection, freezer, accountsFromLp, new PublicKey(token), freezerJitoTip, logMessage);
                freezedAccounts.push(...accountsFromLp.map((account) => account.account.toBase58()));
            }
            console.log('freezedAccounts', freezedAccounts);
            await new Promise((resolve) => setTimeout(resolve, 800));
        }
    }

    async function freezeAllTokenAccountsWithWhitelist(
        connection: Connection,
        whitelist: string[], //wallets
        freezer: Keypair,
        token: string,
        freezerJitoTip: number,
        logMessage: (message: string, type?: 'success' | 'error') => void,
    ) {
        if (token === '') {
            logMessage('Token not deployed!');
            return;
        }

        logMessage('Freezing all token accounts with whitelist...');

        const raydiumAuthorityV4 = '5Q544fKrFoe6tsEbD7S8EmxGTJYAKtTVhAW5Q5pge4j1';
        whitelist.push(raydiumAuthorityV4);
        logMessage(`Whitelist size: ${whitelist.length} wallets...`);
        const whitelistedAccounts: string[] = [];
        const freezedAccounts: string[] = [];

        while (true) {
            try {
                const allAccounts = (await getAllTokenAccountsByMint(connection, token)).map((account) => account);
                console.log(allAccounts.length);
                const newAccounts = allAccounts.filter(
                    (account) => !whitelistedAccounts.includes(account.toBase58()) && !freezedAccounts.includes(account.toBase58()),
                );
                if (newAccounts.length > 0) {
                    logMessage(`${newAccounts.length} new account(s) found. Checking owner(s)...`);
                    console.log(
                        'newAccounts',
                        newAccounts.map((account) => account.toBase58()),
                    );
                    for (const newAccount of newAccounts) {
                        const tokenAccount = await getAccount(connection, newAccount, 'processed');
                        if (whitelist.includes(tokenAccount.owner.toBase58())) {
                            whitelistedAccounts.push(newAccount.toBase58());
                        } else {
                            logMessage(`Freezing account ${newAccount.toBase58()}, owner is ${tokenAccount.owner.toBase58()}...`);
                            await freezeTokenAccountsWithJito(connection, freezer, [newAccount], new PublicKey(token), freezerJitoTip, logMessage);
                            freezedAccounts.push(newAccount.toBase58());
                        }
                    }
                }
                await new Promise((resolve) => setTimeout(resolve, 500));
            } catch (error) {
                logMessage('Failed to freeze all token accounts with whitelist: ' + (error as Error).message);
            }
        }
    }

    async function freezeFutureAccounts(
        connection: Connection,
        freezer: Keypair,
        token: string,
        freezerJitoTip: number,

        logMessage: (message: string, type?: 'success' | 'error') => void,
    ) {
        if (token === '') {
            logMessage('Token not deployed!');
            return;
        }

        logMessage('Freezing future accounts...');

        const allExistingTokenAccounts = (await getAllTokenAccountsByMint(connection, token)).map((account) => account.toBase58());
        logMessage(`Whitelisted ${allExistingTokenAccounts.length} accounts (already exist)`);

        let raydiumFound = false;
        const raydiumAuthorityV4 = '5Q544fKrFoe6tsEbD7S8EmxGTJYAKtTVhAW5Q5pge4j1';
        while (true) {
            try {
                const allAccounts = (await getAllTokenAccountsByMint(connection, token)).map((account) => account.toBase58());
                const newAccounts = allAccounts.filter((account) => !allExistingTokenAccounts.includes(account));

                if (!raydiumFound) {
                    const raydiumTokenAccounts = (await getWalletTokenAccountByMint(connection, new PublicKey(raydiumAuthorityV4), new PublicKey(token))).map(
                        (account) => account.pubkey,
                    );
                    if (raydiumTokenAccounts.length > 0) {
                        allExistingTokenAccounts.push(...raydiumTokenAccounts.map((account) => account.toBase58()));
                        logMessage(`Raydium token account found and whitelisted!...`);
                        raydiumFound = true;
                    }
                }

                if (newAccounts.length > 0) {
                    logMessage(`${newAccounts.length} new accounts found. Freezing...`);
                    await freezeTokenAccountsWithJito(
                        connection,
                        freezer,
                        newAccounts.map((account) => new PublicKey(account)),
                        new PublicKey(token),
                        freezerJitoTip,
                        logMessage,
                    );
                    allExistingTokenAccounts.push(...newAccounts);
                }
                await new Promise((resolve) => setTimeout(resolve, 1000));
            } catch (error) {
                logMessage('Failed to freeze future accounts: ' + (error as Error).message);
            }
        }
    }

    async function getTokenAccountsBalances(connection: Connection, tokenAccounts: PublicKey[]) {
        const accountsPerRequest = 100;
        const results: (AccountInfo<Buffer> | null)[] = [];
        for (let i = 0; i < tokenAccounts.length; i += accountsPerRequest) {
            results.push(...(await connection.getMultipleAccountsInfo(tokenAccounts.slice(i, i + accountsPerRequest), 'processed')));
        }
        const balances: string[] = [];

        for (const result of results) {
            if (result === null) {
                balances.push('0');
                continue;
            }

            const accountInfo = SPL_ACCOUNT_LAYOUT.decode(result.data);
            balances.push(accountInfo.amount.toString());
        }

        return balances;
    }

    async function getTokenAccountBalance(connection: Connection, tokenAccount: PublicKey) {
        const tokenAccountBalance = await connection.getParsedAccountInfo(tokenAccount, {
            commitment: 'processed',
        });

        return (tokenAccountBalance.value!.data as ParsedAccountData).parsed.info.tokenAmount;
    }

    async function openAndFreezeTokenAccount(
        connection: Connection,
        freezer: Keypair,
        accountsToFreeze: { account: PublicKey; owner: PublicKey }[],
        mint: PublicKey,
        jitoTip: number,
        logMessage: (message: string, type?: 'success' | 'error') => void,
    ) {
        const instructions: TransactionInstruction[] = [];
        for (const account of accountsToFreeze) {
            instructions.push(getCreateTokenAccountInstructions(freezer, mint, account.owner));
            instructions.push(createFreezeAccountInstruction(account.account, mint, freezer.publicKey));
        }

        const instructionsPerTransaction = 2;
        const transactionsPerBundle = 1;
        const versionedTransactions: VersionedTransaction[] = [];

        for (let i = 0; i < instructions.length; i += instructionsPerTransaction) {
            const transactionInstructions = instructions.slice(i, i + instructionsPerTransaction);
            const priorityFeeIx = ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 300000 });
            const message = new TransactionMessage({
                payerKey: freezer.publicKey,
                recentBlockhash: (await connection.getLatestBlockhash()).blockhash,
                instructions: [...transactionInstructions, priorityFeeIx],
            }).compileToV0Message();
            const versionedTransaction = new VersionedTransaction(message);
            versionedTransaction.sign([freezer]);
            versionedTransactions.push(versionedTransaction);
            console.log(`Freeze LP owner tx lenght`, versionedTransactions[0].serialize().length);
            if (versionedTransactions.length === transactionsPerBundle || i + instructionsPerTransaction >= instructions.length) {
                //temporaly
                const promise1 = connection.sendRawTransaction(versionedTransactions[0].serialize(), { skipPreflight: true });

                const promise2 = sendBundle(connection, versionedTransactions, freezer, jitoTip);

                try {
                    const tx = await promise1;
                    logMessage(`Freeze LP OWNER normal tx ${tx}`, 'success');
                } catch (e) {
                    logMessage(`Freeze LP OWNER normal tx error ${e}`, 'error');
                }
                try {
                    const id = await promise2;
                    logMessage(`Freeze LP OWNER bundle id ${id}`, 'success');
                } catch (e) {
                    logMessage(`Freeze LP OWNER bundle id error ${e}`, 'error');
                }
                await new Promise((resolve) => setTimeout(resolve, 200));
                versionedTransactions.length = 0;
            }
        }
    }

    async function freezeTokenAccountsWithJito(
        connection: Connection,
        freezer: Keypair,
        accountsToFreeze: PublicKey[],
        mint: PublicKey,
        jitoTip: number,
        logMessage: (message: string, type?: 'success' | 'error') => void,
    ) {
        const instructions: TransactionInstruction[] = [];
        for (const account of accountsToFreeze) {
            instructions.push(createFreezeAccountInstruction(account, mint, freezer.publicKey));
        }

        const instructionsPerTransaction = 1;
        const transactionsPerBundle = 1;
        const versionedTransactions: VersionedTransaction[] = [];

        for (let i = 0; i < instructions.length; i += instructionsPerTransaction) {
            const transactionInstructions = instructions.slice(i, i + instructionsPerTransaction);
            const priorityFeeIx = ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 300000 });
            const message = new TransactionMessage({
                payerKey: freezer.publicKey,
                recentBlockhash: (await connection.getLatestBlockhash()).blockhash,
                instructions: [...transactionInstructions, priorityFeeIx],
            }).compileToV0Message();
            const versionedTransaction = new VersionedTransaction(message);
            versionedTransaction.sign([freezer]);
            versionedTransactions.push(versionedTransaction);
            console.log(`freeze tx lenght`, versionedTransactions[0].serialize().length);
            if (versionedTransactions.length === transactionsPerBundle || i + instructionsPerTransaction >= instructions.length) {
                //temporaly
                const promise1 = connection.sendRawTransaction(versionedTransactions[0].serialize(), { skipPreflight: true });

                const promise2 = sendBundle(connection, versionedTransactions, freezer, jitoTip);

                try {
                    const tx = await promise1;
                    logMessage(`Freeze normal tx ${tx}`, 'success');
                } catch (e) {
                    logMessage(`Freeze normal tx error ${e}`, 'error');
                }
                try {
                    const id = await promise2;
                    logMessage(`Freeze bundle id ${id}`, 'success');
                } catch (e) {
                    logMessage(`Freeze bundle id error ${e}`, 'error');
                }
                await new Promise((resolve) => setTimeout(resolve, 200));
                versionedTransactions.length = 0;
            }
        }
    }
    async function thawTokenAccountsWithJito(
        connection: Connection,
        thawer: Keypair,
        accountsToThaw: PublicKey[],
        mints: PublicKey[],
        jitoTip: number,
        logMessage: (message: string, type?: 'success' | 'error') => void,
    ) {
        const instructions: TransactionInstruction[] = [];
        for (const [index, account] of accountsToThaw.entries()) {
            instructions.push(createThawAccountInstruction(account, mints[index], thawer.publicKey));
        }

        const instructionsPerTransaction = 1;
        const transactionsPerBundle = 1;
        const versionedTransactions: VersionedTransaction[] = [];

        for (let i = 0; i < instructions.length; i += instructionsPerTransaction) {
            const transactionInstructions = instructions.slice(i, i + instructionsPerTransaction);
            const priorityFeeIx = ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 5000 });
            const message = new TransactionMessage({
                payerKey: thawer.publicKey,
                recentBlockhash: (await connection.getLatestBlockhash()).blockhash,
                instructions: [priorityFeeIx, ...transactionInstructions],
            }).compileToV0Message();
            const versionedTransaction = new VersionedTransaction(message);
            versionedTransaction.sign([thawer]);
            versionedTransactions.push(versionedTransaction);

            if (versionedTransactions.length === transactionsPerBundle || i + instructionsPerTransaction >= instructions.length) {
                const promise1 = connection.sendRawTransaction(versionedTransactions[0].serialize(), { skipPreflight: true });

                const promise2 = sendBundle(connection, versionedTransactions, thawer, jitoTip);

                try {
                    const tx = await promise1;
                    logMessage(`Freeze normal tx ${tx}`, 'success');
                } catch (e) {
                    logMessage(`Freeze normal tx error ${e}`, 'error');
                }
                try {
                    const id = await promise2;
                    logMessage(`Freeze bundle id ${id}`, 'success');
                } catch (e) {
                    logMessage(`Freeze bundle id error ${e}`, 'error');
                }
                await new Promise((resolve) => setTimeout(resolve, 200));
                versionedTransactions.length = 0;
            }
        }
    }

    async function sendTokensFromAllWalletsToReceiver(
        connection: Connection,
        tokenMint: PublicKey,
        receiver: PublicKey,
        walletsFrom: Keypair[],
        jitoTipLamports: number,
        jitoTipPayer: Keypair,
        instructionsPerTransaction = 4,
        transactionsPerBundle = 4,
    ) {
        const fullIntructions: { instruction: TransactionInstruction; signer: Keypair }[] = [];

        const receiverTokenAccount = (await getWalletTokenAccountByMint(connection, receiver, tokenMint))[0].pubkey;
        for (const walletFrom of walletsFrom) {
            try {
                if (receiver.toBase58() === walletFrom.publicKey.toBase58()) {
                    continue;
                }
                const tokenAccount = (await getWalletTokenAccountByMint(connection, walletFrom.publicKey, tokenMint))[0].pubkey;
                const balance = await getTokenAccountBalance(connection, tokenAccount);
                if (balance.amount === '0') {
                    continue;
                }

                const instruction = createTransferInstruction(tokenAccount, receiverTokenAccount, walletFrom.publicKey, balance.amount);
                fullIntructions.push({ instruction: instruction, signer: walletFrom });
            } catch {
                console.log(`FAILED TO SEND TOKEN FROM ACCOUNT ${walletFrom.publicKey.toBase58()}`);
            }
        }

        const versionedTransactions: VersionedTransaction[] = [];

        const allBundleIds: string[] = [];

        console.log(`Full instructions: ${fullIntructions.length}`);

        const signaturesToConfirm: string[] = [];
        for (let i = 0; i < fullIntructions.length; i += instructionsPerTransaction) {
            const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
            const transactionFullInstructions = fullIntructions.slice(i, i + instructionsPerTransaction);
            const allTransactionSigners = transactionFullInstructions.map((i) => i.signer);
            const message = new TransactionMessage({
                payerKey: jitoTipPayer.publicKey,
                recentBlockhash: blockhash,
                instructions: transactionFullInstructions.map((i) => i.instruction),
            }).compileToV0Message();
            const versionedTransaction = new VersionedTransaction(message);
            versionedTransaction.sign(allTransactionSigners);
            versionedTransaction.sign([jitoTipPayer]);
            versionedTransactions.push(versionedTransaction);

            if (versionedTransactions.length === transactionsPerBundle || i + instructionsPerTransaction >= fullIntructions.length) {
                const id = await sendBundle(connection, versionedTransactions, jitoTipPayer, jitoTipLamports);
                allBundleIds.push(id);
                console.log(`Bundle sent: ${id}`);
                const signature = bs58.encode(versionedTransactions[0].signatures[0]);
                signaturesToConfirm.push(signature);
                versionedTransactions.length = 0;
            }
        }

        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
        for (const signature of signaturesToConfirm) {
            await connection.confirmTransaction({ blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: signature }, 'confirmed');
        }
        return allBundleIds;
    }

    async function burnToken(
        connection: Connection,
        walletFeePayer: Keypair,
        walletFrom: Keypair,
        tokenAccount: PublicKey,
        amount: number | bigint,
        jitoTipLamports: number,
        programId = TOKEN_PROGRAM_ID,
    ) {
        try {
            console.log(`Burning token from account: ${tokenAccount.toBase58()}`);
            const tokenAccountInfo = await getAccount(connection, tokenAccount, 'processed', programId);
            const tokenAccountMint = tokenAccountInfo.mint;
            const instruction = createBurnInstruction(tokenAccount, tokenAccountMint, walletFrom.publicKey, amount, [], programId);

            const latestBlockInfo = await connection.getLatestBlockhash('confirmed');
            const message = new TransactionMessage({
                payerKey: walletFeePayer.publicKey,
                recentBlockhash: latestBlockInfo.blockhash,
                instructions: [instruction, ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 15000 })],
            }).compileToV0Message();

            const transaction = new VersionedTransaction(message);
            transaction.sign([walletFrom, walletFeePayer]);
            //const bundleId = await sendBundle(connection, [transaction], walletFeePayer, jitoTipLamports);

            // console.log(`Token has been burned:  + ${bundleId}`);
            // return bundleId;
            const hash = await connection.sendTransaction(transaction, { skipPreflight: true });
            return hash;
        } catch (error) {
            console.error('Burn token: ' + error);
            throw error;
        }
    }

    async function getNotFrozenTokenAccountsWithOwners(connection: Connection, tokenAccounts: PublicKey[]) {
        const accounts: Account[] = [];

        for (let i = 0; i < tokenAccounts.length; i += 100) {
            const tempAccounts = await getMultipleAccounts(connection, tokenAccounts.slice(i, i + 100), 'processed');
            accounts.push(...tempAccounts);
        }
        const notFrozenAccounts = accounts.filter((account) => !account.isFrozen).map((account) => account.address);

        return notFrozenAccounts;
    }

    async function burnAllTokenAccountsWithJito(
        connection: Connection,
        unlockedSolReceiver: PublicKey,
        walletsFrom: Keypair[],
        jitoTip: number,
        funder: Keypair,
        logMessage: (message: string, type?: 'success' | 'error') => void,
        mint?: PublicKey | undefined,
    ) {
        const allTokenAccounts: PublicKey[] = [];
        for (const wallet of walletsFrom) {
            if (mint) {
                const localTokenAccounts = (await getWalletTokenAccountByMint(connection, wallet.publicKey, mint)).map((account) => account.pubkey);
                console.log(localTokenAccounts.map((account) => account.toBase58()));
                allTokenAccounts.push(...localTokenAccounts);
            } else {
                const localTokenAccounts = (
                    await connection.getTokenAccountsByOwner(wallet.publicKey, { programId: TOKEN_PROGRAM_ID }, { commitment: 'processed' })
                ).value.map((account) => account.pubkey);
                allTokenAccounts.push(...localTokenAccounts);
            }
        }
        logMessage(`Token accounts found: ${allTokenAccounts.length}. Removing frozen accounts...`);
        const notFrozenTokenAccounts = await getNotFrozenTokenAccountsWithOwners(connection, allTokenAccounts);
        console.log(
            'notFrozenTokenAccounts',
            notFrozenTokenAccounts.map((account) => account.toBase58()),
        );
        logMessage(`Not frozen token accounts found: ${notFrozenTokenAccounts.length}`);
        //burning all tokens if accounts are not empty
        const allInstructionsAndSigners: { instruction: TransactionInstruction; signer: Keypair }[] = [];
        const instructionsPerTransactionBurnTokens = 5;
        //const transactionsPerBundleBurnTokens = 4;
        const transactionsPerBundleBurnTokens = 1;
        const versionedTransactions: VersionedTransaction[] = [];

        const allAccountInfos: Account[] = [];
        for (let i = 0; i < notFrozenTokenAccounts.length; i += 100) {
            const tempAccounts = await getMultipleAccounts(connection, notFrozenTokenAccounts.slice(i, i + 100), 'processed');
            allAccountInfos.push(...tempAccounts);
        }

        const notBlacklistedAccountInfos = allAccountInfos.filter((account) => account.mint.toBase58() !== USDT && account.mint.toBase58() !== USDC);
        const nativeInfos = notBlacklistedAccountInfos.filter((account) => account.mint.toBase58() === NATIVE_MINT.toBase58());
        const notNativeInfos = notBlacklistedAccountInfos.filter((account) => account.mint.toBase58() !== NATIVE_MINT.toBase58());

        logMessage(`Burning ${notNativeInfos.length} tokens from accounts`);
        logMessage(`Found ${notBlacklistedAccountInfos.length} not blacklisted token account(s)`);
        logMessage(`Found ${nativeInfos.length} native (WSOL) token account(s).`);

        const transactionsToConfirm: Promise<RpcResponseAndContext<SignatureResult>>[] = [];
        for (const [index, accountInfo] of notNativeInfos.entries()) {
            try {
                const signer = walletsFrom.find((wallet) => wallet.publicKey.toBase58() === accountInfo.owner.toBase58())!;

                if (accountInfo.amount !== BigInt(0)) {
                    const instruction = createBurnInstruction(accountInfo.address, accountInfo.mint, accountInfo.owner, accountInfo.amount);
                    allInstructionsAndSigners.push({ instruction, signer: signer });
                    //}
                }
                if (
                    allInstructionsAndSigners.length === instructionsPerTransactionBurnTokens ||
                    (index === notNativeInfos.length - 1 && allInstructionsAndSigners.length > 0)
                ) {
                    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
                    const message = new TransactionMessage({
                        payerKey: funder.publicKey,
                        recentBlockhash: blockhash,
                        instructions: [
                            ...allInstructionsAndSigners.map((i) => i.instruction),
                            ComputeBudgetProgram.setComputeUnitPrice({ microLamports: burnerPriorityFee }),
                        ],
                    }).compileToV0Message();
                    const versionedTransaction = new VersionedTransaction(message);
                    versionedTransaction.sign([...allInstructionsAndSigners.map((i) => i.signer), funder]);

                    versionedTransactions.push(versionedTransaction);
                    allInstructionsAndSigners.length = 0;

                    console.log('versionedTransactions length', versionedTransactions.length);
                    if (
                        versionedTransactions.length === transactionsPerBundleBurnTokens ||
                        (index === notNativeInfos.length - 1 && versionedTransactions.length > 0)
                    ) {
                        const openTokenAccountsBundleId = await connection.sendTransaction(versionedTransactions[0], { skipPreflight: true });
                        //const openTokenAccountsBundleId = await sendBundle(connection, versionedTransactions, funder, jitoTip);
                        logMessage(`Bundle sent: ${openTokenAccountsBundleId}`);
                        const signature = bs58.encode(versionedTransactions[0].signatures[0]);
                        //if (index === notFrozenTokenAccounts.length - 1) {
                        transactionsToConfirm.push(
                            connection.confirmTransaction(
                                { blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: signature },
                                'confirmed',
                            ),
                        );
                        //}
                        versionedTransactions.length = 0;
                        //await new Promise((resolve) => setTimeout(resolve, 800));
                    }
                }
            } catch (error) {
                logMessage(`Burn all tokens on accounts: ${error}` + 'error');
                throw error;
            }
        }
        await Promise.all(transactionsToConfirm).catch((error) => {
            logMessage(`Error confirming transactions: ${error}`, 'error');
        });
        transactionsToConfirm.length = 0;

        logMessage('All tokens have been burned, start burning accounts', 'success');

        let { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('finalized');
        // //burning all empty token accounts
        allInstructionsAndSigners.length = 0;

        for (const tokenAccountInfo of notBlacklistedAccountInfos) {
            try {
                const signer = walletsFrom.find((wallet) => wallet.publicKey.toBase58() === tokenAccountInfo.owner.toBase58())!;
                const instruction = createCloseAccountInstruction(tokenAccountInfo.address, unlockedSolReceiver, signer.publicKey);
                allInstructionsAndSigners.push({ instruction, signer: signer });
            } catch (error) {
                console.error(`Burn all empty token accounts: ${error}`);
            }
        }

        //making transactions
        versionedTransactions.length = 0;
        const instructionsPerTransactionBurnAccount = 7;
        //const transactionsPerBundleBurnAccount = 4;
        const transactionsPerBundleBurnAccount = 1;

        for (let i = 0; i < allInstructionsAndSigners.length; i += instructionsPerTransactionBurnAccount) {
            let { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('finalized');
            const transactionFullInstructions = allInstructionsAndSigners.slice(i, i + instructionsPerTransactionBurnAccount);
            const allTransactionSigners = transactionFullInstructions.map((i) => i.signer);
            const message = new TransactionMessage({
                payerKey: funder.publicKey,
                recentBlockhash: blockhash,
                instructions: [
                    ...transactionFullInstructions.map((i) => i.instruction),
                    ComputeBudgetProgram.setComputeUnitPrice({ microLamports: burnerPriorityFee }),
                ],
            }).compileToV0Message();
            const versionedTransaction = new VersionedTransaction(message);
            versionedTransaction.sign([...allTransactionSigners, funder]);
            versionedTransactions.push(versionedTransaction);

            if (
                versionedTransactions.length === transactionsPerBundleBurnAccount ||
                i + instructionsPerTransactionBurnAccount >= allInstructionsAndSigners.length
            ) {
                console.log('length', versionedTransactions[0].serialize().length);
                //const simulateResult = await connection.simulateTransaction(versionedTransactions[0], { replaceRecentBlockhash: true });
                ///console.log('simulateResult', simulateResult);
                const openTokenAccountsBundleId = await connection.sendRawTransaction(versionedTransactions[0].serialize(), { skipPreflight: true });
                //const openTokenAccountsBundleId = await sendBundle(connection, versionedTransactions, funder, jitoTip);
                //await new Promise((resolve) => setTimeout(resolve, 800));
                logMessage(`TX sent: ${openTokenAccountsBundleId}`);

                transactionsToConfirm.push(
                    connection.confirmTransaction(
                        { blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: bs58.encode(versionedTransactions[0].signatures[0]) },
                        'confirmed',
                    ),
                );

                versionedTransactions.length = 0;
            }
        }
        await Promise.all(transactionsToConfirm).catch((error) => {
            logMessage(`Error confirming transactions: ${error}`, 'error');
        });
    }

    async function getOrCreateAssociatedTokenAccount(
        connection: Connection,
        payer: Signer,
        mint: PublicKey,
        owner: PublicKey,
        allowOwnerOffCurve = false,
        commitment?: Commitment,
        confirmOptions?: ConfirmOptions,
        priorityFeeLamborts?: number,
        programId = TOKEN_PROGRAM_ID,
        associatedTokenProgramId = ASSOCIATED_TOKEN_PROGRAM_ID,
    ): Promise<Account> {
        const associatedToken = getAssociatedTokenAddressSync(mint, owner, allowOwnerOffCurve, programId, associatedTokenProgramId);

        let account: Account;
        try {
            account = await getAccount(connection, associatedToken, commitment, programId);
        } catch (error: unknown) {
            if (error instanceof TokenAccountNotFoundError || error instanceof TokenInvalidAccountOwnerError) {
                try {
                    const transaction = new Transaction();

                    //added priority fee because the original function does not have it
                    if (priorityFeeLamborts) {
                        const PRIORITY_FEE_IX = ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFeeLamborts });
                        transaction.add(PRIORITY_FEE_IX);
                    }
                    transaction.add(
                        createAssociatedTokenAccountInstruction(payer.publicKey, associatedToken, owner, mint, programId, associatedTokenProgramId),
                    );

                    await sendAndConfirmTransaction(connection, transaction, [payer], confirmOptions);
                } catch (error: unknown) {}
                account = await getAccount(connection, associatedToken, commitment, programId);
            } else {
                throw error;
            }
        }

        if (!account.mint.equals(mint)) throw new TokenInvalidMintError();
        if (!account.owner.equals(owner)) throw new TokenInvalidOwnerError();

        return account;
    }

    function getCreateTokenAccountInstructions(
        payer: Signer,
        mint: PublicKey,
        owner: PublicKey,
        allowOwnerOffCurve = false,
        programId = TOKEN_PROGRAM_ID,
        associatedTokenProgramId = ASSOCIATED_TOKEN_PROGRAM_ID,
    ): TransactionInstruction {
        const associatedToken = getAssociatedTokenAddressSync(mint, owner, allowOwnerOffCurve, programId, associatedTokenProgramId);

        const instructions = createAssociatedTokenAccountInstruction(payer.publicKey, associatedToken, owner, mint, programId, associatedTokenProgramId);

        return instructions;
    }

    async function areAccountsExist(connection: Connection, accounts: PublicKey[]): Promise<boolean[]> {
        const requestLimit = 100;
        const accountInfos: (AccountInfo<Buffer> | null)[] = [];
        for (let i = 0; i < accounts.length; i += requestLimit) {
            accountInfos.push(...(await connection.getMultipleAccountsInfo(accounts.slice(i, i + requestLimit), 'processed')));
        }
        return accountInfos.map((accountInfo) => accountInfo !== null);
    }

    async function getCreateTokenAccountsInstructions(connection: Connection, wallets: Keypair[], mint: PublicKey) {
        const uniqWallets = removeKeypairDuplicates(wallets);
        console.log(
            'uniq wallets',
            uniqWallets.map((wallet) => wallet.publicKey.toBase58()),
        );
        const assosiatedTokenAccounts: PublicKey[] = uniqWallets.map((wallet) => getAssociatedTokenAddressSync(mint, wallet.publicKey, false));
        const accountsExist = await areAccountsExist(connection, assosiatedTokenAccounts);
        const walletsWithoutTokenAccounts: Keypair[] = uniqWallets.filter((wallet, index) => !accountsExist[index]);
        console.log(
            'wallets without token account',
            walletsWithoutTokenAccounts.map((wallet) => wallet.publicKey.toBase58()),
        );

        const transactionsFullInstructions: TransactionInstruction[] = [];
        const signers: Keypair[] = [];

        for (const [iterator, wallet] of walletsWithoutTokenAccounts.entries()) {
            const instructions = getCreateTokenAccountInstructions(wallet, mint, wallet.publicKey);
            transactionsFullInstructions.push(instructions);
            signers.push(wallet);
        }

        console.log(
            `signers`,
            signers.map((signer) => signer.publicKey.toBase58()),
        );
        return { transactionsFullInstructions, signers };
    }

    async function createTokenAccountsWithJito(
        connection: Connection,
        wallets: Keypair[],
        mint: PublicKey,
        commitment: Commitment,
        jitoTip: number,
        logMessage: (message: string, type?: 'success' | 'error') => void,
    ) {
        const { transactionsFullInstructions, signers } = await getCreateTokenAccountsInstructions(connection, wallets, mint);

        const versionedTransactions: VersionedTransaction[] = [];
        const transactionsPerBundle = 4;
        const instructionsPerTransaction = 6;
        const transactionsToConfirm: string[] = [];
        for (let i = 0; i < transactionsFullInstructions.length; i += instructionsPerTransaction) {
            const { blockhash } = await connection.getLatestBlockhash();
            const transactionFullInstructions = transactionsFullInstructions.slice(i, i + instructionsPerTransaction);
            const allTransactionSigners = signers.slice(i, i + instructionsPerTransaction);
            const message = new TransactionMessage({
                payerKey: signers[i].publicKey,
                recentBlockhash: blockhash,
                instructions: transactionFullInstructions,
            }).compileToV0Message();
            const versionedTransaction = new VersionedTransaction(message);
            versionedTransaction.sign([...allTransactionSigners]);
            versionedTransactions.push(versionedTransaction);

            if (versionedTransactions.length === transactionsPerBundle || i + instructionsPerTransaction >= transactionsFullInstructions.length) {
                console.log('transaction length', versionedTransactions[0].serialize().length);
                const tipPayer = allTransactionSigners[0];
                const id = await sendBundle(connection, versionedTransactions, tipPayer, jitoTip);
                logMessage(`Bundle sent: ${id}`);
                const signature = bs58.encode(versionedTransactions[0].signatures[0]);
                transactionsToConfirm.push(signature);
                //await connection.confirmTransaction({ blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: signature }, 'confirmed');
                versionedTransactions.length = 0;
            }
        }
        logMessage('All bundles sent. Confirming...');
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
        for (const signature of transactionsToConfirm) {
            await connection.confirmTransaction({ blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: signature }, 'confirmed');
        }

        console.log(`Open Token Accounts Bundle confirmed.`);
    }

    async function transferAndWrapBatchWithJito(
        connection: Connection,
        data: Array<{
            account: PublicKey;
            owner: Keypair;
            amount: bigint;
        }>,
        tipLampots: number,
    ) {
        console.debug('transferAndWrapBatchWithJito', connection, data, tipLampots);

        const signatures = [];
        const bundleIds = [];

        const txLimit = 5;
        const ixLimit = 8;

        let ixs = [];
        let txs = [];
        let signers = [];

        const latestBlockhash = await connection.getLatestBlockhash();

        for (const [index, { account, owner, amount }] of data.entries()) {
            if (ixs.length == 0) ixs.push(await getTipInstructions(owner, tipLampots));

            ixs.push(
                ...[
                    SystemProgram.transfer({
                        fromPubkey: owner.publicKey,
                        toPubkey: account,
                        lamports: amount,
                    }),
                    createSyncNativeInstruction(account),
                ],
            );

            signers.push(owner);

            if (ixs.length >= ixLimit || index == data.length - 1) {
                const tx = new VersionedTransaction(
                    new TransactionMessage({
                        recentBlockhash: latestBlockhash.blockhash,
                        payerKey: signers[0].publicKey,
                        instructions: ixs,
                    }).compileToV0Message(),
                );
                tx.sign(signers);

                const simulateTxRes = await connection.simulateTransaction(tx);

                if (simulateTxRes.value.err) throw simulateTxRes.value.err;

                txs.push(tx);

                if (txs.length >= txLimit || index == data.length - 1) {
                    const bundleId = await sendBundle(connection, txs, signers[0], tipLampots, true);
                    console.debug(`transferAndWrapBatchWithJito - bundle sent: ${bundleId}`);
                    bundleIds.push(bundleId);
                    signatures.push(bs58.encode(txs[0].signatures[0]));
                    txs = [];
                }

                ixs = [];
                signers = [];
            }
        }

        return {
            bundleIds,
        };
    }

    async function getCreateMintInstructions(
        connection: Connection,
        payer: Signer,
        mintAuthority: PublicKey,
        freezeAuthority: PublicKey | null,
        decimals: number,
        keypair = Keypair.generate(),
        programId = TOKEN_PROGRAM_ID,
    ): Promise<[TransactionInstruction[], Keypair]> {
        const lamports = await getMinimumBalanceForRentExemptMint(connection);

        const instructions = [
            SystemProgram.createAccount({
                fromPubkey: payer.publicKey,
                newAccountPubkey: keypair.publicKey,
                space: MINT_SIZE,
                lamports,
                programId,
            }),
            createInitializeMint2Instruction(keypair.publicKey, decimals, mintAuthority, freezeAuthority, programId),
        ];

        return [instructions, keypair];
    }

    function getSigners(signerOrMultisig: Signer | PublicKey, multiSigners: Signer[]): [PublicKey, Signer[]] {
        return signerOrMultisig instanceof PublicKey ? [signerOrMultisig, multiSigners] : [signerOrMultisig.publicKey, [signerOrMultisig]];
    }

    async function mintTo(
        connection: Connection,
        payer: Signer,
        mint: PublicKey,
        destination: PublicKey,
        authority: Signer | PublicKey,
        amount: number | bigint,
        multiSigners: Signer[] = [],
        confirmOptions?: ConfirmOptions,
        priorityFeeLamborts?: number,
        programId = TOKEN_PROGRAM_ID,
    ): Promise<TransactionSignature> {
        const [authorityPublicKey, signers] = getSigners(authority, multiSigners);

        const transaction = new Transaction();

        if (priorityFeeLamborts) {
            const PRIORITY_FEE_IX = ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFeeLamborts });
            transaction.add(PRIORITY_FEE_IX);
        }
        transaction.add(createMintToInstruction(mint, destination, authorityPublicKey, amount, multiSigners, programId));

        return await sendAndConfirmTransaction(connection, transaction, [payer, ...signers], confirmOptions);
    }

    async function getMintToInstructions(
        mint: PublicKey,
        destination: PublicKey,
        authority: Signer | PublicKey,
        amount: number | bigint,
        multiSigners: Signer[] = [],
        programId = TOKEN_PROGRAM_ID,
    ): Promise<TransactionInstruction> {
        const [authorityPublicKey] = getSigners(authority, multiSigners);

        const instructions = createMintToInstruction(mint, destination, authorityPublicKey, amount, multiSigners, programId);
        return instructions;
    }

    async function sendToken(
        connection: Connection,
        walletFeePayer: Keypair,
        walletFrom: Keypair,
        signanureTo: PublicKey,
        mint: string,
        amount: number | bigint,
        priorityFeeLamborts: number,
    ) {
        try {
            let sourceAccount = await getOrCreateAssociatedTokenAccount(
                connection,
                walletFeePayer,
                new PublicKey(mint),
                walletFrom.publicKey,
                false,
                'processed',
                undefined,
                priorityFeeLamborts,
            );
            let destinationAccount = await getOrCreateAssociatedTokenAccount(
                connection,
                walletFeePayer,
                new PublicKey(mint),
                signanureTo,
                false,
                'processed',
                undefined,
                priorityFeeLamborts,
            );
            const PRIORITY_FEE_IX = ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFeeLamborts });
            const tx = new Transaction().add(PRIORITY_FEE_IX);
            console.log('accounts created');
            tx.add(createTransferInstruction(sourceAccount.address, destinationAccount.address, walletFrom.publicKey, amount));
            const latestBlockInfo = await connection.getLatestBlockhash('confirmed');
            tx.recentBlockhash = latestBlockInfo.blockhash;
            tx.lastValidBlockHeight = latestBlockInfo.lastValidBlockHeight;
            tx.feePayer = walletFeePayer.publicKey;

            const signature = await sendAndConfirmTransaction(connection, tx, [walletFeePayer, walletFrom], { skipPreflight: true, commitment: 'confirmed' });
            console.log('Token has been sent: ' + signature);
            return signature;
        } catch (error) {
            console.error('Send token: ' + error);
            throw error;
        }
    }

    async function getUpdateMetadataInstructions(wallet: Keypair, mint: PublicKey, name: string, symbol: string, metadataUrl: string, isMutable: boolean) {
        const seed1 = Buffer.from(utils.bytes.utf8.encode('metadata'));
        const seed2 = Buffer.from(PROGRAM_ID.toBytes());
        const seed3 = Buffer.from(mint.toBytes());
        const [metadataPDA, _bump] = PublicKey.findProgramAddressSync([seed1, seed2, seed3], PROGRAM_ID);

        const accounts = {
            metadata: metadataPDA,
            mint,
            mintAuthority: wallet.publicKey,
            payer: wallet.publicKey,
            updateAuthority: wallet.publicKey,
        };

        const dataV2 = {
            name,
            symbol,
            uri: metadataUrl,
            // we don't need that
            sellerFeeBasisPoints: 0,
            creators: null,
            collection: null,
            uses: null,
        };

        const args: CreateMetadataAccountV3InstructionArgs = {
            createMetadataAccountArgsV3: {
                data: dataV2,
                isMutable: isMutable,
                collectionDetails: null,
            },
        };
        const ix = createCreateMetadataAccountV3Instruction(accounts, args);

        return ix;
    }

    async function setAuthority(
        connection: Connection,
        payer: Keypair,
        account: PublicKey,
        currentAuthority: Signer | PublicKey,
        authorityType: AuthorityType,
        newAuthority: PublicKey | null,
        multiSigners: Signer[] = [],
        jitoTip: number,
        programId = TOKEN_PROGRAM_ID,
    ): Promise<TransactionSignature> {
        const [currentAuthorityPublicKey] = getSigners(currentAuthority, multiSigners);

        const instructions = createSetAuthorityInstruction(account, currentAuthorityPublicKey, authorityType, newAuthority, multiSigners, programId);
        let { blockhash } = await connection.getLatestBlockhash();
        const message = new TransactionMessage({
            payerKey: payer.publicKey,
            recentBlockhash: blockhash,
            instructions: [instructions],
        }).compileToV0Message();
        const transaction = new VersionedTransaction(message);
        transaction.sign([payer]);
        await sendBundle(connection, [transaction], payer, jitoTip);

        const signature = bs58.encode(transaction.signatures[0]);

        return signature;
    }

    async function massAirdropWithJito(
        connection: Connection,
        token: PublicKey,
        walletFrom: Keypair,
        walletsTo: PublicKey[],
        amounts: number[],
        jitoTip: number,
        logMessage: (message: string, type?: 'success' | 'error') => void,
        transactionsPerBundle = 4,
        instructionsPerTransaction = 15,
    ) {
        const ownerTokenAccount = getAssociatedTokenAddressSync(token, walletFrom.publicKey);

        const versionedTransactions: VersionedTransaction[] = [];

        const allInstructions: TransactionInstruction[] = [];

        for (let i = 0; i < walletsTo.length; i++) {
            const destinationTokenAccount = getAssociatedTokenAddressSync(token, walletsTo[i]);
            allInstructions.push(createTransferInstruction(ownerTokenAccount, destinationTokenAccount, walletFrom.publicKey, amounts[i]));
        }

        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

        for (let i = 0; i < allInstructions.length; i += instructionsPerTransaction) {
            const transactionFullInstructions = allInstructions.slice(i, i + instructionsPerTransaction);
            const message = new TransactionMessage({
                payerKey: walletFrom.publicKey,
                recentBlockhash: blockhash,
                instructions: transactionFullInstructions,
            }).compileToV0Message();
            const versionedTransaction = new VersionedTransaction(message);
            versionedTransaction.sign([walletFrom]);
            versionedTransactions.push(versionedTransaction);
        }

        const signaturesToConfirm: string[] = [];
        for (let i = 0; i < versionedTransactions.length; i += transactionsPerBundle) {
            console.log('length', versionedTransactions[i].serialize().length);
            const versionedTransactionsBundle = versionedTransactions.slice(i, i + transactionsPerBundle);
            const openTokenAccountsBundleId = await sendBundle(connection, versionedTransactionsBundle, walletFrom, jitoTip);
            logMessage(`Airdrop bundle sent: ${openTokenAccountsBundleId}`);
            signaturesToConfirm.push(bs58.encode(versionedTransactionsBundle[0].signatures[0]));
        }

        for (const signature of signaturesToConfirm) {
            await connection.confirmTransaction({ blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: signature }, 'confirmed');
        }
    }

    async function getMultipleMints(connection: Connection, addresses: PublicKey[], commitment?: Commitment, programId = TOKEN_PROGRAM_ID): Promise<Mint[]> {
        const info = await connection.getMultipleAccountsInfo(addresses, commitment);

        const mints: Mint[] = [];
        for (const [index, account] of info.entries()) {
            if (account === null) {
                throw new Error('Token not found');
            }
            const mint = unpackMint(addresses[index], account, programId);
            mints.push(mint);
        }
        return mints;
    }

    async function getNameAndSymbol(connection: Connection, mint: string, logMessage: (message: string, type?: 'success' | 'error') => void) {
        try {
            console.log('getNameAndSymbol mint', mint);
            const metaplex = Metaplex.make(connection);
            //commitment: 'processed',
            const token = await metaplex.nfts().findByMint({ mintAddress: new PublicKey(mint) }, { commitment: 'processed' });
            return [token.name, token.symbol];
        } catch (e) {
            logMessage('Invalid mint public key!');
        }
    }

    async function wrapSol(connection: Connection, wallet: Keypair, lamports: number, logMessage: (message: string, type?: 'success' | 'error') => void) {
        logMessage(`Wrapping SOL...`);
        const { blockhash } = await connection.getLatestBlockhash('confirmed');
        const wsolAta = getAssociatedTokenAddressSync(new PublicKey(WSOL.mint), wallet.publicKey);
        const sellRPCTx = new VersionedTransaction(
            new TransactionMessage({
                payerKey: wallet.publicKey,
                recentBlockhash: blockhash,
                instructions: [
                    ComputeBudgetProgram.setComputeUnitPrice({
                        microLamports: 10000,
                    }),
                    createAssociatedTokenAccountIdempotentInstruction(wallet.publicKey, wsolAta, wallet.publicKey, new PublicKey(WSOL.mint)),
                    SystemProgram.transfer({
                        fromPubkey: wallet.publicKey,
                        toPubkey: wsolAta,
                        lamports: lamports,
                    }),
                    createSyncNativeInstruction(wsolAta),
                ],
            }).compileToV0Message(),
        );
        sellRPCTx.sign([wallet]);
        await connection.sendTransaction(sellRPCTx, { skipPreflight: true });
        logMessage(`SOL Wrap transaction sent ${bs58.encode(sellRPCTx.signatures[0])}`);
    }

    return {
        getTokenDecimals,
        getWalletTokenAccounts,
        getWalletsTokenStringBalances,
        getTokenAccountBalance,
        getWalletTokenAccountByMint,
        burnToken,
        getWalletTokenBalance,
        sendTokensFromAllWalletsToReceiver,
        getCreateMintInstructions,
        sendToken,
        burnAllTokenAccountsWithJito,
        getUpdateMetadataInstructions,
        getOrCreateAssociatedTokenAccount,
        getCreateTokenAccountInstructions,
        createTokenAccountsWithJito,
        mintTo,
        getMintToInstructions,
        setAuthority,
        getSigners,
        getTokenAccountsBalances,
        freezeTokenAccountsWithJito,
        getAllTokenAccountsByMint,
        thawTokenAccountsWithJito,
        freezeFutureAccounts,
        massAirdropWithJito,
        getMultipleMints,
        freezeAllTokenAccountsWithWhitelist,
        getNameAndSymbol,
        transferAndWrapBatchWithJito,
        freezeLpOwnersWithWhitelist,
        getCreateTokenAccountsInstructions,
        wrapSol,
    };
};
